<script lang="ts">
	import type { FilterDataProperty, ITableData, sortDataType, sortDirectionType } from './types';
	import WatchlistTableRow from 'components/CoinListTable/CoinListTableRow/CoinListTableRow.svelte';
	import EmptyTable from 'components/EmptyTable/EmptyTable.svelte';
	import SortingArrow from 'components/SortingArrow/SortingArrow.svelte';
	import type { FilterIntervalType } from 'customTypes/filterTypes';
	import { _ } from 'utils/i18/i18n';
	import userStore from 'stores/user';
	import { sortNumber } from 'utils/sortTableData.js';
	import { getTableInterval } from 'utils/tableInterval';

	export let tableData: ITableData[];
	export let filterInterval: FilterIntervalType;
	export let dark = false;
	export let list: string[] = [];
	export let listName: string;

	type SparklineObject = {
		[key: string]: Array<number>;
	};
	export let sparklines: SparklineObject = {};

	let pressedIconId: string | number;
	let isPressed = true;

	function handleClick(e: Event & { currentTarget: HTMLElement }) {
		pressedIconId = e.currentTarget.id;
	}

	function sortData(property: FilterDataProperty, type: sortDataType, direction: sortDirectionType) {
		let newArr = tableData;
		if (type === 'text') {
			newArr = tableData?.sort(function (a, b) {
				return direction === 'descend'
					? a[property].localeCompare(b[property])
					: b[property].localeCompare(a[property]);
			});
		}

		if (type === 'value') {
			newArr = tableData?.sort(function (a, b) {
				return direction === 'descend'
					? Number(a[property]) - Number(b[property])
					: Number(b[property]) - Number(a[property]);
			});
		}
		tableData = [...newArr];
	}
</script>

<div class="wrapper" class:dark>
	<div class="table-wrapper">
		<table class="table">
			<thead class="table-head no-select">
				<tr class="table-row">
					<th
						id="filterRank"
						class="head-cell rank clickable"
						on:click={(e) => {
							pressedIconId !== 'filterRank' ? (isPressed = true) : null;
							handleClick(e);
							isPressed ? sortData('rank', 'value', 'descend') : sortData('rank', 'value', 'ascend');
							isPressed = !isPressed;
						}}
						># <span class="rank-text">{$_('watchlist.rank')}</span>
						<div class="icon-wrapper mobile-hide">
							<SortingArrow {isPressed} {pressedIconId} idToCompare={'filterRank'} width={11} height={7} />
						</div></th
					>
					<th
						id="filterName"
						class="head-cell name clickable"
						on:click={(e) => {
							pressedIconId !== 'filterName' ? (isPressed = true) : null;
							handleClick(e);
							isPressed ? sortData('name', 'text', 'descend') : sortData('name', 'text', 'ascend');
							isPressed = !isPressed;
						}}
					>
						{$_('watchlist.name')}
						<div class="icon-wrapper">
							<SortingArrow {isPressed} {pressedIconId} idToCompare={'filterName'} width={11} height={7} />
						</div>
					</th>
					<th class="head-cell symbol">{$_('watchlist.symbol')}</th>
					<th
						id="filterPrice"
						class="head-cell price clickable"
						on:click={(e) => {
							pressedIconId !== 'filterPrice' ? (isPressed = true) : null;
							handleClick(e);
							isPressed ? sortData('price', 'value', 'descend') : sortData('price', 'value', 'ascend');
							isPressed = !isPressed;
						}}
						>{$_('general.price')}
						<div class="icon-wrapper">
							<SortingArrow
								{isPressed}
								{pressedIconId}
								idToCompare={'filterPrice'}
								width={11}
								height={7}
							/>
						</div></th
					>
					<th class="head-cell change-price"
						>{getTableInterval(filterInterval)} {$_('general.price')}
					</th>
					<th class="head-cell change-volume"
						>{getTableInterval(filterInterval)} {$_('general.volume')}
					</th>
					<th class="head-cell liquidity graph"
						>{$_('watchlist.trend')} {getTableInterval(filterInterval)}
					</th>
					<th
						id="filterMarketCap"
						class="head-cell market-cap clickable"
						on:click={(e) => {
							if (pressedIconId !== 'filterMarketCap') isPressed = true;
							handleClick(e);
							tableData = sortNumber(tableData, 'marketCap', isPressed ? 'ascend' : 'descend');
							isPressed = !isPressed;
						}}
						>{$_('general.marketCap')}
						<div class="icon-wrapper">
							<SortingArrow
								{isPressed}
								{pressedIconId}
								idToCompare={'filterMarketCap'}
								width={11}
								height={7}
							/>
						</div></th
					>
				</tr>
			</thead>
			<tbody class="table-body">
				{#if tableData.length === 0}
					<EmptyTable {dark} colAmountFromTabletView={8} colAmountMobView={7} tableType="watchlist" />
				{:else}
					{#each tableData as item (item.id)}
						<WatchlistTableRow
							data={item}
							{dark}
							isInWatchlist={true}
							{filterInterval}
							bind:list
							{listName}
							sparkline={sparklines[item.id] || sparklines[item.symbol]}
							user={$userStore}
						/>
					{/each}
				{/if}
			</tbody>
		</table>
	</div>
</div>

<style lang="scss">
	@import 'src/components/CoinListTable/coinListTableHead';
	.wrapper {
		margin-bottom: 60px;
	}
</style>
